import React, { useCallback, useMemo, useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, Stack, useTheme } from '@mui/material';
import _omit from 'lodash/omit';
import { NumberParam, type QueryParamConfigMap, StringParam, withDefault } from 'serialize-query-params';
import { useQueryParams } from 'use-query-params';

import {
  FolderType,
  ProcessDTO,
  ProcessQueryDTO,
  ProcessResponseDTO,
  useFilterProcessesInfinite,
  useGetProcessesHook
} from '@/api/generated';
import DataGrid from '@/components/DataGrid/DataGrid';
import { AutocompleteWide } from '@/components/fields/Autocomplete';
import { getRelevantParentFolder } from '@/components/fields/Location/locationTreeUtils';
import Form from '@/components/Form/Form';
import StickyHead from '@/components/StickyHead/StickyHead';
import NewProcessButton from '@/containers/Processes/NewProcessButton';
import { processColumns } from '@/containers/Processes/processColumns';
import { useFolderPermissions } from '@/hooks/permissions/useFolderPermissions';
import { DEFAULT_PAGINATION, DEFAULT_SORTING, useEntityList } from '@/hooks/useEntityList';
import { useOrganizationStructure } from '@/hooks/useOrganizationStructure';
import { toProcess } from '@/root/linker';
import { CreateProcessModal } from '@/views/CreateProcessModal/CreateProcessModal';

const DEFAULT_VALUES = {
  ...DEFAULT_SORTING,
  ...DEFAULT_PAGINATION
};

const ALLOWED_LOCATION_TYPES: FolderType[] = [FolderType.FOLDER, FolderType.SUB_FOLDER];

const URL_CONFIG_MAP = {
  size: withDefault(NumberParam, DEFAULT_PAGINATION.size),
  locationSlug: withDefault(StringParam, '')
} as QueryParamConfigMap;

const OrganizationProcesses = () => {
  const theme = useTheme();
  const { t } = useTranslation(['processes', 'filters']);
  const [queryBody, setQueryBody] = useState<ProcessQueryDTO | null>(null);
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);
  const { organizationSlug } = useParams() as { organizationSlug: string };
  const autosuggestProcesses = useGetProcessesHook();
  const { mapStructure, location: currentLocation } = useOrganizationStructure(locationSlug!);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const isAllowedLocation = ALLOWED_LOCATION_TYPES.includes(currentLocation?.type as FolderType);

  const relevantParentFolder = useMemo(
    () => getRelevantParentFolder(currentLocation!, mapStructure),
    [currentLocation, mapStructure]
  );

  const { value: { canCreateProcesses } = {} } = useFolderPermissions({
    variables: {
      organizationSlug,
      locationSlug: relevantParentFolder?.slug,
      locationType: relevantParentFolder?.type
    }
  });

  const shouldShowNewProcessButton = isAllowedLocation && canCreateProcesses;
  const {
    data: processesData,
    isError,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useFilterProcessesInfinite(queryBody ?? {}, {
    query: { suspense: false, enabled: !!queryBody }
  });

  const processesList = useMemo(
    () => (processesData?.pages?.flatMap(page => page) || []) as ProcessResponseDTO[],
    [processesData]
  );

  const handleFetchNextPage = useCallback(() => {
    // TODO: Pagination is turned off in case of error. Later we should at least notify the user that there was one.
    if (isError || isFetchingNextPage || !hasNextPage || !processesData?.pages?.length) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isError, isFetchingNextPage, processesData?.pages?.length]);

  const transformRequest = useCallback(
    (request: FieldValues & { locationSlug?: string }) => {
      // eslint-disable-next-line i18next/no-literal-string
      const updatedRequest = _omit(request, ['locationSlug']);
      const location = mapStructure[request.locationSlug!];

      return (
        location &&
        ({
          ...updatedRequest,
          process: {
            locations: [
              {
                slug: location.slug,
                type: location.type
              }
            ]
          }
          // TODO: Fix backend location field improper type
        } as ProcessQueryDTO)
      );
    },
    [mapStructure]
  );

  const { form, sorting, handleChangeSort } = useEntityList<FieldValues, ProcessQueryDTO>(
    DEFAULT_VALUES as FieldValues,
    URL_CONFIG_MAP,
    setQueryBody,
    transformRequest,
    300
  );

  const handleSearchProcesses = useCallback(
    async (query: string) => {
      try {
        const searchResults = await autosuggestProcesses({ search: query });

        return searchResults.map(({ id, name }: ProcessDTO) => ({
          label: name!,
          value: id!,
          to: toProcess({ processId: id! })
        }));
      } catch (e) {
        return [];
      }
    },
    [autosuggestProcesses]
  );

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const handleOpenCreateModal = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const columns = useMemo(() => processColumns(theme, t), [theme, t]);

  return (
    <Box sx={{ px: 5, flexGrow: 1 }} component="div">
      <StickyHead sx={{ paddingTop: theme.spacing(2.25) }}>
        <Form form={form} className="Filters-root">
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
            <AutocompleteWide
              placeholder={t('layouts.processes.autosuggest.placeholder')}
              loadOptions={handleSearchProcesses}
            />
            {shouldShowNewProcessButton && <NewProcessButton onClick={handleOpenCreateModal} />}
          </Stack>
        </Form>
      </StickyHead>
      <DataGrid
        data={processesList || []}
        columns={columns}
        onSort={handleChangeSort}
        isLoading={isLoading || !queryBody}
        isLoadingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        sorting={sorting}
        resizeWatchSelector=".Filters-root"
        hasError={isError && !processesList.length}
        onLoadNextPage={handleFetchNextPage}
      />
      {showCreateModal && (
        <CreateProcessModal
          onClose={handleCloseCreateModal}
          defaultOrganizationSlug={organizationSlug}
          defaultLocationSlug={locationSlug}
        />
      )}
    </Box>
  );
};

export default OrganizationProcesses;
